import React, { useState } from 'react'
import { Select, Tooltip } from 'antd'
import {
  getRolePickerGroups,
  getVenueRoleSelectValue,
} from 'src/utils/venueRoleCapabilities'

const { Option, OptGroup } = Select

export const VENUE_ROLE_SELECT_DROPDOWN_MAX_HEIGHT = 320
export const VENUE_ROLE_SELECT_Z_INDEX = 1100

export const VENUE_ROLE_SELECT_DROPDOWN_STYLE = {
  maxHeight: VENUE_ROLE_SELECT_DROPDOWN_MAX_HEIGHT,
  overflow: 'auto',
  zIndex: VENUE_ROLE_SELECT_Z_INDEX,
}

export const VENUE_ROLE_SELECT_MENU_STYLE = {
  maxHeight: VENUE_ROLE_SELECT_DROPDOWN_MAX_HEIGHT,
  overflow: 'auto',
}

export const getVenueRoleSelectPopupContainer = triggerNode => {
  if (typeof document !== 'undefined' && document.body) {
    return document.body
  }
  return triggerNode?.parentElement || triggerNode
}

const VenueRoleSelect = ({
  value,
  onChange,
  customRoles = [],
  disabled = false,
  size,
  style,
  dropdownStyle,
  dropdownMenuStyle,
  getPopupContainer,
  dropdownMatchSelectWidth = false,
  dropdownClassName,
  tooltipTitle,
  onDropdownVisibleChange,
  lockCustomRoles = false,
  onLockedCustomRole,
  ...rest
}) => {
  const [dropdownOpen, setDropdownOpen] = useState(false)
  const [suppressTooltip, setSuppressTooltip] = useState(false)
  const groups = getRolePickerGroups(customRoles, { lockCustomRoles })

  const handleDropdownVisibleChange = open => {
    const isOpen = Boolean(open)
    setDropdownOpen(isOpen)
    if (isOpen) setSuppressTooltip(true)
    onDropdownVisibleChange?.(open)
  }

  const handleChange = nextValue => {
    const option = groups
      .flatMap(group => group.options)
      .find(item => item.value === nextValue)
    if (option?.locked) {
      onLockedCustomRole?.()
      return
    }
    onChange?.(nextValue)
  }

  const selectValue = getVenueRoleSelectValue(value, customRoles)

  const select = (
    <Select
      {...rest}
      value={selectValue}
      onChange={handleChange}
      disabled={disabled}
      size={size}
      style={style}
      optionLabelProp='label'
      dropdownMatchSelectWidth={dropdownMatchSelectWidth}
      getPopupContainer={getPopupContainer || getVenueRoleSelectPopupContainer}
      dropdownStyle={{ ...VENUE_ROLE_SELECT_DROPDOWN_STYLE, ...dropdownStyle }}
      dropdownMenuStyle={{
        ...VENUE_ROLE_SELECT_MENU_STYLE,
        ...dropdownMenuStyle,
      }}
      dropdownClassName={
        dropdownClassName
          ? `${dropdownClassName} venue-role-select-dropdown`
          : 'venue-role-select-dropdown'
      }
      onDropdownVisibleChange={handleDropdownVisibleChange}
    >
      {groups.map(group => (
        <OptGroup key={group.label} label={group.label}>
          {group.options.map(option => (
            <Option
              key={option.value}
              value={option.value}
              label={option.label}
              title={
                [option.description, option.chipSummary]
                  .filter(Boolean)
                  .join(' ') || option.label
              }
            >
              {option.label}
              {option.description ? (
                <span style={{ color: '#8c8c8c' }}> {option.description}</span>
              ) : null}
              {option.chipSummary ? (
                <span style={{ color: '#8c8c8c' }}> {option.chipSummary}</span>
              ) : null}
            </Option>
          ))}
        </OptGroup>
      ))}
    </Select>
  )

  if (!tooltipTitle) return select

  const hideTooltip = dropdownOpen || suppressTooltip

  return (
    <Tooltip
      title={hideTooltip ? '' : tooltipTitle}
      destroyTooltipOnHide
      getPopupContainer={getVenueRoleSelectPopupContainer}
    >
      <span
        data-role-tooltip-suppressed={hideTooltip ? 'true' : 'false'}
        style={{ display: 'block', width: '100%' }}
        onMouseLeave={() => setSuppressTooltip(false)}
      >
        {select}
      </span>
    </Tooltip>
  )
}

export default VenueRoleSelect
